import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import {
    HOST_RECRUITMENT_BANNER_DISMISSED_KEY,
    HOST_RECRUITMENT_MODAL_VIEWED_KEY,
    LocalStorageAccessService,
} from '@app/core/_services/local-storage-access.service'
import { SystemBannerService } from '@app/core/_services/system-banner.service'
import { UserService } from '@app/core/_services/user.service'
import { SharedModule } from '@app/shared/shared.module'
import { SystemBannerComponent } from '../system-banner/system-banner.component'
import { DialogService } from '@app/core/_services/dialog.service'
import { TimeSyncBannerComponent } from '../time-sync-banner/time-sync-banner.component'
import { ServerTimeService } from '@app/core/_services/server-time.service'
import { MODAL_VERIFY_EMAIL } from '@app/shared-dialogs/email-verify/email-verify-dialog.component'
import { HostRecruitmentBannerComponent } from '../host-recruitment-banner/host-recruitment-banner.component'
import { GroupMeetingHostBetaBannerComponent } from '../group-meeting-host-beta-banner/group-meeting-host-beta-banner.component'
import { EmailVerificationBannerComponent } from '../email-verification-banner/email-verification-banner.component'
import { SuspendedBannerComponent } from '../suspended-banner/suspended-banner.component'
import { GroupMeetingBetaLaunchHostAnnouncementBannerComponent } from '../group-meeting-beta-launch-host-announcement-banner/group-meeting-beta-launch-host-announcement-banner.component'
import { GroupMeetingBetaLiveHostAnnouncementBannerComponent } from '../group-meeting-beta-live-host-announcement-banner/group-meeting-beta-live-host-announcement-banner.component'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { filter, fromEvent, map, merge } from 'rxjs'

const GROUP_MEETINGS_BETA_LAUNCH_DATE = [2026, 7, 10, 9]
const GROUP_MEETINGS_BETA_LAUNCH_TIME_ZONE = 'Europe/Berlin'
const GROUP_MEETINGS_BETA_LIVE_ANNOUNCEMENT_END_DATE = [2026, 7, 14]

@Component({
    selector: 'fm-banners',
    imports: [
        CommonModule,
        TimeSyncBannerComponent,
        SharedModule,
        SystemBannerComponent,
        HostRecruitmentBannerComponent,
        GroupMeetingHostBetaBannerComponent,
        GroupMeetingBetaLaunchHostAnnouncementBannerComponent,
        GroupMeetingBetaLiveHostAnnouncementBannerComponent,
        EmailVerificationBannerComponent,
        SuspendedBannerComponent,
    ],
    templateUrl: './banners.component.html',
    styleUrl: './banners.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannersComponent {
    @Input() showMaintenanceBanner: boolean = false

    private hostRecruitmentBannerDismissed = signal(
        this.localStorageAccessService.getItem(HOST_RECRUITMENT_BANNER_DISMISSED_KEY) === 'true',
    )
    private hostRecruitmentModalViewed =
        this.localStorageAccessService.getItem(HOST_RECRUITMENT_MODAL_VIEWED_KEY) === 'true'
    private groupMeetingsHostBetaBannerDismissed = signal(false)
    private readonly groupMeetingsBetaAnnouncementCurrentTimeMs = toSignal(
        merge(
            fromEvent(window, 'focus'),
            fromEvent(document, 'visibilitychange').pipe(
                filter(() => document.visibilityState === 'visible'),
            ),
        ).pipe(map(() => Date.now())),
        { initialValue: Date.now() },
    )
    public showHostRecruitmentBanner = computed(() => {
        const user = this.userService.currentUserSig()
        return (
            !!user?.userId &&
            this.featureCheckService.isHostRecruitmentEnabled(user) &&
            !this.featureCheckService.groupMeetingsCreateEnabledSig() &&
            this.hostRecruitmentModalViewed &&
            !this.hostRecruitmentBannerDismissed()
        )
    })
    public showGroupMeetingsHostBetaBanner = computed(() => {
        const user = this.userService.currentUserSig()
        return (
            !!user?.userId &&
            this.featureCheckService.groupMeetingsCreateEnabledSig() &&
            this.featureCheckService.hasViewedGroupMeetingsHostBetaModal(user) &&
            !this.featureCheckService.hasDismissedGroupMeetingsHostBetaBanner(user) &&
            !this.groupMeetingsHostBetaBannerDismissed()
        )
    })
    public showGroupMeetingsBetaLaunchHostAnnouncementBanner = computed(() => {
        const user = this.userService.currentUserSig()
        const timeZone = user?.timeZone
        if (
            !this.featureCheckService.groupMeetingsCreateEnabledSig() ||
            !timeZone ||
            !moment.tz.zone(timeZone)
        ) {
            return false
        }
        const launchDate = moment
            .tz(GROUP_MEETINGS_BETA_LAUNCH_DATE, GROUP_MEETINGS_BETA_LAUNCH_TIME_ZONE)
            .valueOf()
        return this.groupMeetingsBetaAnnouncementCurrentTimeMs() < launchDate
    })
    public showGroupMeetingsBetaLiveHostAnnouncementBanner = computed(() => {
        const user = this.userService.currentUserSig()
        const timeZone = user?.timeZone
        if (
            !this.featureCheckService.groupMeetingsCreateEnabledSig() ||
            !timeZone ||
            !moment.tz.zone(timeZone)
        ) {
            return false
        }

        const currentTimeMs = this.groupMeetingsBetaAnnouncementCurrentTimeMs()
        const launchDate = moment
            .tz(GROUP_MEETINGS_BETA_LAUNCH_DATE, GROUP_MEETINGS_BETA_LAUNCH_TIME_ZONE)
            .valueOf()
        const announcementEndDate = moment
            .tz(GROUP_MEETINGS_BETA_LIVE_ANNOUNCEMENT_END_DATE, timeZone)
            .valueOf()
        return currentTimeMs >= launchDate && currentTimeMs < announcementEndDate
    })

    constructor(
        public serverTimeService: ServerTimeService,
        private dialogService: DialogService,
        private featureCheckService: FeatureCheckService,
        private localStorageAccessService: LocalStorageAccessService,
        public systemBannerService: SystemBannerService,
        public userService: UserService,
    ) {}

    launchVerify() {
        this.dialogService.openEmailVerificationDialog(MODAL_VERIFY_EMAIL)
    }

    launchSuspended() {
        this.dialogService.openSuspendedDialog()
    }

    sendEmailVerification() {
        this.userService.sentEmailVerification.next(true)
        this.userService.recheckEmailVerified().then((verified) => {
            if (!verified) {
                this.userService.sendEmailVerification().then((result) => {})
            }
        })
    }

    dismissHostRecruitmentBanner(): void {
        this.localStorageAccessService.setItem(HOST_RECRUITMENT_BANNER_DISMISSED_KEY, 'true')
        this.hostRecruitmentBannerDismissed.set(true)
    }

    dismissGroupMeetingsHostBetaBanner(): void {
        const user = this.userService.currentUserSig()
        if (!user?.userId) {
            return
        }

        this.groupMeetingsHostBetaBannerDismissed.set(true)
        this.featureCheckService.setHasDismissedGroupMeetingsHostBetaBanner(user)
    }
}
